import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { LightbulbIcon } from 'lucide-react';
import { Button, ErrorNotice, Field, ImageUploadField, SelectInput, TextArea, TextInput } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage, uploadMedia } from '@/lib/api';
import { FEEDBACK_KINDS, FEEDBACK_KIND_LABELS, fileFeedback, type FeedbackKind } from '@/lib/feedback';

/** The lightbulb in the top bar: an idea, feedback, or a bug, filed from wherever the person is. */
export function FeedbackButton(): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label="Ideas and feedback"
        title="Ideas and feedback"
        onClick={() => setOpen(true)}
        className="relative flex size-9 shrink-0 items-center justify-center rounded-portal-pill border border-portal-line text-portal-muted transition-colors hover:border-brand hover:text-brand"
      >
        <LightbulbIcon aria-hidden className="size-4" />
      </button>
      {open ? <FeedbackDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

const KIND_HINT: Readonly<Record<FeedbackKind, string>> = {
  idea: 'Something the app could do.',
  feedback: 'How something works for you, good or bad.',
  bug: 'Something broken. Say what you did and what happened.',
};

function FeedbackDialog({ onClose }: { readonly onClose: () => void }): React.ReactElement {
  const location = useLocation();
  const page = `${window.location.origin}${location.pathname}${location.search}`;
  const [kind, setKind] = useState<FeedbackKind>('feedback');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const save = useMutation({
    mutationFn: () => fileFeedback({ kind, title: title.trim(), details: details.trim(), page, screenshotUrl: screenshotUrl.trim() }),
    onSuccess: () => {
      toast.success('Thanks, we have it.');
      onClose();
    },
  });
  const valid = title.trim().length > 0 && details.trim().length > 0 && !save.isPending;

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-lg">
        <DialogTitle>Ideas and feedback</DialogTitle>
        <p className="text-portal-small text-portal-muted">Tell the team what to build, what to change, or what broke. It goes straight to the people working on the app.</p>
        <form
          className="mt-4 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <Field label="What is this" htmlFor="feedback-kind" required hint={KIND_HINT[kind]}>
            <SelectInput id="feedback-kind" value={kind} onChange={(event) => setKind(event.target.value as FeedbackKind)}>
              {FEEDBACK_KINDS.map((key) => (
                <option key={key} value={key}>
                  {FEEDBACK_KIND_LABELS[key]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Title" htmlFor="feedback-title" required hint="One line, the way you would say it out loud.">
            <TextInput id="feedback-title" value={title} maxLength={200} placeholder={kind === 'bug' ? 'The packing list forgets my quantity' : 'Let me sort events by client'} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="Details" htmlFor="feedback-details" required hint={kind === 'bug' ? 'What you did, what you expected, what happened instead.' : 'Why it matters and what it would look like.'}>
            <TextArea id="feedback-details" rows={4} value={details} maxLength={5000} onChange={(event) => setDetails(event.target.value)} />
          </Field>
          <ImageUploadField label="Screenshot" description="A picture of the screen helps, especially for a bug." value={screenshotUrl} onChange={setScreenshotUrl} upload={async (file) => (await uploadMedia(file, { kind: 'feedback' })).url} />
          <p className="text-portal-micro text-portal-muted">Filed from this page: {location.pathname}{location.search}</p>
          {save.isError ? <ErrorNotice inline title="Not sent" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid}>
              {save.isPending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
